import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  computed,
  effect,
  inject,
} from '@angular/core';

import { A11yReadingAid, UserStore } from '@signal-store';

/**
 * Reading aid overlay: a horizontal guide bar or a dimming mask that tracks
 * the mouse pointer. Helps users with dyslexia/ADHD keep their place.
 *
 * The mousemove listener runs outside Angular and is throttled with
 * requestAnimationFrame; positioning uses CSS transform to stay on the
 * compositor. Respects Reduce motion via :host-context in the SCSS.
 */
@Component({
  selector: 'cipo-a11y-reading-aid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (mode()) {
      @case ('guide') {
        <div class="a11y-reading-guide" aria-hidden="true">
          <div class="a11y-reading-guide__line"></div>
          <div class="a11y-reading-guide__caret"></div>
        </div>
      }
      @case ('mask') {
        <div class="a11y-reading-mask a11y-reading-mask--top" aria-hidden="true"></div>
        <div class="a11y-reading-mask a11y-reading-mask--bottom" aria-hidden="true"></div>
      }
    }
  `,
  styleUrl: './a11y-reading-aid.component.scss',
})
export class A11yReadingAidComponent {
  private readonly userStore = inject(UserStore);
  private readonly host = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly zone = inject(NgZone);

  /** Half the height of the clear strip in `mask` mode. */
  private static readonly STRIP_HALF = 24;
  /** Half-width of the guide line. */
  private static readonly LINE_HALF_WIDTH = 280;

  readonly mode = computed(() => this.userStore.a11y().readingAid);

  // Snapshot updated inside effect() (reactive context) so render() — which
  // runs in a RAF callback outside Angular — never reads a signal directly.
  private currentMode: A11yReadingAid = 'off';

  private pointerY = typeof window === 'undefined' ? 0 : window.innerHeight / 2;
  private pointerX = typeof window === 'undefined' ? 0 : window.innerWidth / 2;
  private rafId = 0;
  private listening = false;

  private readonly onMove = (event: MouseEvent) => {
    this.pointerY = event.clientY;
    this.pointerX = event.clientX;
    this.scheduleRender();
  };

  constructor() {
    const destroyRef = inject(DestroyRef);

    effect(() => {
      // Read the signal here (reactive context) and snapshot it for render().
      this.currentMode = this.mode();
      this.toggleListener(this.currentMode !== 'off');
      // Position the (possibly just-created) elements on the next frame.
      this.scheduleRender();
    });

    destroyRef.onDestroy(() => {
      this.toggleListener(false);
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    });
  }

  private toggleListener(on: boolean) {
    if (on === this.listening) {
      return;
    }
    this.listening = on;
    if (on) {
      this.zone.runOutsideAngular(() => document.addEventListener('mousemove', this.onMove, { passive: true }));
    } else {
      document.removeEventListener('mousemove', this.onMove);
    }
  }

  private scheduleRender() {
    if (this.rafId) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.render();
      });
    });
  }

  private render() {
    const root = this.host.nativeElement;

    if (this.currentMode === 'guide') {
      const guide = root.querySelector<HTMLElement>('.a11y-reading-guide');
      const caret = root.querySelector<HTMLElement>('.a11y-reading-guide__caret');
      if (guide) {
        const vw = window.innerWidth;
        const half = A11yReadingAidComponent.LINE_HALF_WIDTH;
        // Clamp so the line never overflows the viewport edges.
        const left = Math.max(half, Math.min(vw - half, this.pointerX));
        guide.style.transform = `translate(${left}px, ${this.pointerY}px)`;

        if (caret) {
          // Map real cursor X onto the line: when not clamped → centre.
          // When clamped → caret slides toward the nearest end of the line.
          const caretLeft = Math.max(0, Math.min(half * 2, half + (this.pointerX - left)));
          caret.style.left = `${caretLeft}px`;
        }
      }
      return;
    }

    if (this.currentMode === 'mask') {
      const top = root.querySelector<HTMLElement>('.a11y-reading-mask--top');
      const bottom = root.querySelector<HTMLElement>('.a11y-reading-mask--bottom');
      const half = A11yReadingAidComponent.STRIP_HALF;

      if (top && bottom) {
        top.style.height = `${Math.max(0, this.pointerY - half)}px`;
        bottom.style.top = `${this.pointerY + half}px`;
      }
    }
  }
}
